import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { showInfoMessage } from 'store/ui/actions'
import {
  fetchReleaseNotes as fetchReleaseNotesRequest,
  fetchUnreadCount as fetchUnreadCountRequest,
  openReleaseNote as openReleaseNoteRequest,
  sendReleaseFeedback as sendReleaseFeedbackRequest,
  fetchAdminReleaseNotes as fetchAdminReleaseNotesRequest,
  createAdminReleaseNote as createAdminReleaseNoteRequest,
  updateAdminReleaseNote as updateAdminReleaseNoteRequest,
  publishAdminReleaseNote as publishAdminReleaseNoteRequest,
  scheduleAdminReleaseNote as scheduleAdminReleaseNoteRequest,
  archiveAdminReleaseNote as archiveAdminReleaseNoteRequest,
  createAiDraftRelease as createAiDraftReleaseRequest,
  openAllReleaseNoteRequest,
} from '../api'

async function extractErrorMessage(err) {
  try {
    const { error } = await err.json()
    return error
  } catch (e) {
    return undefined
  }
}

export const fetchReleaseNotes = createAsyncThunk(
  'releaseNotes/fetchReleaseNotes',
  async (params = {}) => fetchReleaseNotesRequest(params)
)

export const fetchUnreadCount = createAsyncThunk(
  'releaseNotes/fetchUnreadCount',
  async () => fetchUnreadCountRequest()
)

export const openReleaseNote = createAsyncThunk(
  'releaseNotes/openReleaseNote',
  async (id) => {
    await openReleaseNoteRequest(id)
    return id
  }
)

export const openAllReleaseNote = createAsyncThunk(
  'releaseNotes/openReleaseNote',
  async () => openAllReleaseNoteRequest()
)

export const sendReleaseFeedback = createAsyncThunk(
  'releaseNotes/sendReleaseFeedback',
  async ({ id, feedbackType, message }) =>
    sendReleaseFeedbackRequest({ id, feedbackType, message })
)

export const fetchAdminReleaseNotes = createAsyncThunk(
  'releaseNotes/fetchAdminReleaseNotes',
  async (_, { getState }) => {
    const { adminPage, adminPerPage, adminFilter } = getState().releaseNotes
    return fetchAdminReleaseNotesRequest({
      page: adminPage,
      perPage: adminPerPage,
      status: adminFilter,
    })
  }
)

export const createAdminReleaseNote = createAsyncThunk(
  'releaseNotes/createAdminReleaseNote',
  async (payload, { dispatch, rejectWithValue }) => {
    try {
      const result = await createAdminReleaseNoteRequest(payload)
      dispatch(fetchAdminReleaseNotes())
      return result
    } catch (err) {
      return rejectWithValue(await extractErrorMessage(err))
    }
  }
)

export const updateAdminReleaseNote = createAsyncThunk(
  'releaseNotes/updateAdminReleaseNote',
  async ({ id, payload }, { dispatch, rejectWithValue }) => {
    try {
      const result = await updateAdminReleaseNoteRequest(id, payload)
      dispatch(fetchAdminReleaseNotes())
      return result
    } catch (err) {
      return rejectWithValue(await extractErrorMessage(err))
    }
  }
)

export const publishAdminReleaseNote = createAsyncThunk(
  'releaseNotes/publishAdminReleaseNote',
  async (id, { dispatch }) => {
    const result = await publishAdminReleaseNoteRequest(id)
    dispatch(showInfoMessage('Опубликовано!'))
    dispatch(fetchAdminReleaseNotes())
    return result
  }
)

export const scheduleAdminReleaseNote = createAsyncThunk(
  'releaseNotes/scheduleAdminReleaseNote',
  async ({ id, payload }, { dispatch }) => {
    const result = await scheduleAdminReleaseNoteRequest(id, payload)
    dispatch(fetchAdminReleaseNotes())
    return result
  }
)

export const archiveAdminReleaseNote = createAsyncThunk(
  'releaseNotes/archiveAdminReleaseNote',
  async (id, { dispatch }) => {
    const result = await archiveAdminReleaseNoteRequest(id)
    dispatch(fetchAdminReleaseNotes())
    return result
  }
)

export const createAiDraftRelease = createAsyncThunk(
  'releaseNotes/createAiDraftRelease',
  async (payload, { dispatch }) => {
    const result = await createAiDraftReleaseRequest(payload)
    dispatch(fetchAdminReleaseNotes())
    return result
  }
)

const initialState = {
  items: [],
  loading: false,
  error: null,
  unreadCount: 0,
  priorityFilter: 'all',

  adminList: [],
  adminLoading: false,
  adminFilter: 'all',
  adminPage: 1,
  adminPerPage: 10,
  adminTotalCount: 0,
  adminActionPending: false,
  aiGenerating: false,
  message: null,
}

const releaseNotesSlice = createSlice({
  name: 'releaseNotes',
  initialState,
  reducers: {
    setPriorityFilter(state, action) {
      state.priorityFilter = action.payload
    },
    setAdminFilter(state, action) {
      state.adminFilter = action.payload
      state.adminPage = 1
    },
    setAdminPage(state, action) {
      state.adminPage = action.payload
    },
    setAdminPerPage(state, action) {
      state.adminPerPage = action.payload
      state.adminPage = 1
    },
    clearMessage(state) {
      state.message = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchReleaseNotes.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchReleaseNotes.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload || []
      })
      .addCase(fetchReleaseNotes.rejected, (state) => {
        state.loading = false
        state.error = 'load_error'
      })

      .addCase(fetchUnreadCount.fulfilled, (state, action) => {
        state.unreadCount = action.payload || 0
      })

      .addCase(openReleaseNote.fulfilled, (state, action) => {
        const item = state.items.find((i) => i.id === action.payload)
        if (item && !item.opened) {
          item.opened = true
          state.unreadCount = Math.max(0, state.unreadCount - 1)
        }
      })

      .addCase(fetchAdminReleaseNotes.pending, (state) => {
        state.adminLoading = true
      })
      .addCase(fetchAdminReleaseNotes.fulfilled, (state, action) => {
        state.adminLoading = false
        state.adminList = action.payload?.items || []
        state.adminTotalCount = action.payload?.totalCount || 0
      })
      .addCase(fetchAdminReleaseNotes.rejected, (state) => {
        state.adminLoading = false
      })

      .addCase(createAdminReleaseNote.pending, (state) => {
        state.adminActionPending = true
      })
      .addCase(createAdminReleaseNote.fulfilled, (state) => {
        state.adminActionPending = false
        state.message = 'draftCreated'
      })
      .addCase(createAdminReleaseNote.rejected, (state) => {
        state.adminActionPending = false
      })

      .addCase(updateAdminReleaseNote.pending, (state) => {
        state.adminActionPending = true
      })
      .addCase(updateAdminReleaseNote.fulfilled, (state) => {
        state.adminActionPending = false
        state.message = 'updated'
      })
      .addCase(updateAdminReleaseNote.rejected, (state) => {
        state.adminActionPending = false
      })

      .addCase(publishAdminReleaseNote.pending, (state) => {
        state.adminActionPending = true
      })
      .addCase(publishAdminReleaseNote.fulfilled, (state) => {
        state.adminActionPending = false
      })
      .addCase(publishAdminReleaseNote.rejected, (state) => {
        state.adminActionPending = false
      })

      .addCase(archiveAdminReleaseNote.pending, (state) => {
        state.adminActionPending = true
      })
      .addCase(archiveAdminReleaseNote.fulfilled, (state) => {
        state.adminActionPending = false
      })
      .addCase(archiveAdminReleaseNote.rejected, (state) => {
        state.adminActionPending = false
      })

      .addCase(createAiDraftRelease.pending, (state) => {
        state.aiGenerating = true
      })
      .addCase(createAiDraftRelease.fulfilled, (state) => {
        state.aiGenerating = false
        state.message = 'aiCreated'
      })
      .addCase(createAiDraftRelease.rejected, (state) => {
        state.aiGenerating = false
        state.message = 'aiError'
      })
  },
})

export const {
  setPriorityFilter,
  setAdminFilter,
  setAdminPage,
  setAdminPerPage,
  clearMessage,
} = releaseNotesSlice.actions

export const releaseNotesReducer = releaseNotesSlice.reducer
export default releaseNotesSlice.reducer
